import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  Save,
  TestTube,
  ExternalLink,
  FileText,
  Building2,
  AlertCircle,
  Key,
  Settings,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import SuperAdminCompanySelector, { useSuperAdminCompany } from '@/components/SuperAdminCompanySelector';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ZohoIntegrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  
  // Super Admin company selector
  const { 
    selectedCompanyId, 
    setSelectedCompanyId, 
    needsCompanySelector, 
    isReady 
  } = useSuperAdminCompany();
  
  const [credentials, setCredentials] = useState({
    client_id: '',
    client_secret: '',
    refresh_token: '',
    organization_id: '',
    data_center: 'com',
    default_purchase_tax_id: ''
  });
  const [stats, setStats] = useState({
    purchaseOrders: 0,
    bills: 0,
    vendors: 0
  });
  // Zoho tax dropdown (auto-populated). Manual entry stays available
  // via the "Enter ID manually" toggle in case the Zoho call fails
  // (legacy creds, scope, etc.).
  const [taxes, setTaxes] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesError, setTaxesError] = useState('');
  const [taxManualEntry, setTaxManualEntry] = useState(false);
  // Self-service grant-code exchanger: user pastes the 10-min code from
  // api-console.zoho.com → Generate Code, we POST to /zoho/exchange-grant-code
  // which calls Zoho's /oauth/v2/token with grant_type=authorization_code,
  // persists the returned refresh_token on the company, and clears the
  // access-token cache. No curl required.
  const [grantCode, setGrantCode] = useState('');
  const [exchangingCode, setExchangingCode] = useState(false);
  // Standalone default-tax picker — lives outside the credentials editor
  // so admins can change the default purchase tax without unlocking edit
  // mode. Mirrors `credentials.default_purchase_tax_id` but has its own
  // setter so the save button knows exactly what to PUT.
  const [defaultTaxId, setDefaultTaxId] = useState('');
  const [savingDefaultTax, setSavingDefaultTax] = useState(false);
  const [defaultTaxManualEntry, setDefaultTaxManualEntry] = useState(false);
  
  useEffect(() => {
    if (isReady) {
      checkStatus();
      loadStats();
      loadCredentials();
      // Preload taxes so the standalone Default Purchase Tax dropdown
      // is populated immediately — admin shouldn't have to click
      // refresh just to see the list.
      loadTaxes();
    } else {
      setLoading(false);
    }
  }, [isReady, selectedCompanyId]);
  
  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/enterprise/zoho/status`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      setStatus({ working: false, error: error.response?.data?.detail || 'Failed to check status' });
    } finally {
      setLoading(false);
    }
  };
  
  const loadCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/enterprise/zoho/credentials`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCredentials({
          client_id: response.data.client_id || '',
          client_secret: '', // Never returned for security
          refresh_token: '', // Never returned for security
          organization_id: response.data.organization_id || '',
          data_center: response.data.data_center || 'com',
          default_purchase_tax_id: response.data.default_purchase_tax_id || ''
        });
        setDefaultTaxId(response.data.default_purchase_tax_id || '');
      }
    } catch (error) {
      console.log('No existing credentials');
    }
  };
  
  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');

      const poRes = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get vendor count
      const vendorRes = await axios.get(`${API_BASE_URL}/enterprise/vendors?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats({
        purchaseOrders: poRes.data.total || 0,
        vendors: vendorRes.data.total || 0,
        bills: 0
      });
    } catch (error) {
      console.log('Stats load failed');
    }
  };

  const loadTaxes = async () => {
    setTaxesLoading(true);
    setTaxesError('');
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/enterprise/zoho/taxes`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setTaxes(res.data?.taxes || []);
    } catch (error) {
      // Common case: Zoho not configured yet (credentials drawer just opened).
      // Don't show a scary toast — just remember the error so the UI can
      // offer a "manual entry" fallback.
      const detail = error?.response?.data?.detail || error?.message || 'Could not fetch taxes from Zoho';
      setTaxesError(detail);
      setTaxes([]);
    } finally {
      setTaxesLoading(false);
    }
  };

  // Standalone "save just the default tax" handler. The credentials
  // editor sends the full payload; this one sends ONLY the tax id so
  // an admin can change the default tax without re-entering the
  // credentials (the May-2026 ask). PUT /zoho/credentials is partial-
  // update by design — missing keys are preserved.
  const handleSaveDefaultTax = async () => {
    setSavingDefaultTax(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/enterprise/zoho/credentials`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      await axios.put(
        url,
        { default_purchase_tax_id: defaultTaxId || '' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Mirror to the credentials state so the editor (if reopened)
      // shows the same value.
      setCredentials((c) => ({ ...c, default_purchase_tax_id: defaultTaxId || '' }));
      toast.success(defaultTaxId ? 'Default purchase tax saved' : 'Default purchase tax cleared');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save default tax');
    } finally {
      setSavingDefaultTax(false);
    }
  };
  
  const handleRefreshToken = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/enterprise/zoho/refresh-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zoho token refreshed successfully');
      checkStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh token');
    } finally {
      setSyncing(false);
    }
  };

  const handleExchangeGrantCode = async () => {
    const code = (grantCode || '').trim();
    if (!code) {
      toast.error('Paste the 10-min grant code from Zoho first');
      return;
    }
    if (!credentials.client_id) {
      toast.error('Save Client ID and Client Secret first, then exchange the grant code');
      return;
    }
    setExchangingCode(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/enterprise/zoho/exchange-grant-code`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      // Forward whatever the user has in the form so they don't have to
      // click "Save Credentials" first. Backend falls back to saved values
      // for any field left blank.
      await axios.post(url, {
        grant_code: code,
        client_id: credentials.client_id || undefined,
        client_secret: credentials.client_secret || undefined,
        data_center: credentials.data_center
          ? (credentials.data_center.startsWith('.') ? credentials.data_center : `.${credentials.data_center}`)
          : undefined,
        organization_id: credentials.organization_id || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Grant code exchanged. Refresh token saved.');
      setGrantCode('');
      await loadCredentials();
      await checkStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to exchange grant code');
    } finally {
      setExchangingCode(false);
    }
  };
  
  const handleSaveCredentials = async () => {
    if (!credentials.client_id || !credentials.organization_id) {
      toast.error('Client ID and Organization ID are required');
      return;
    }
    
    // Super admin must select a company
    if (needsCompanySelector && !selectedCompanyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/enterprise/zoho/credentials`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      await axios.put(url, credentials, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zoho credentials saved successfully');
      setShowCredentials(false);
      checkStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSyncVendors = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/enterprise/zoho/sync-vendors`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Vendors synced successfully');
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync vendors');
    } finally {
      setSyncing(false);
    }
  };
  
  const isConnected = status?.working === true;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/integrations')}
          className="text-wt-text-muted hover:text-wt-text-primary"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Integrations
        </Button>
      </div>
      
      {/* Super Admin Company Selector */}
      <SuperAdminCompanySelector
        selectedCompanyId={selectedCompanyId}
        onCompanyChange={setSelectedCompanyId}
        integrationName="Zoho Books"
      />
      
      {/* Show message if super admin hasn't selected a company */}
      {needsCompanySelector && !selectedCompanyId ? (
        <div className="text-center py-12 text-wt-text-muted">
          <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Please select a company above to configure Zoho Books integration.</p>
        </div>
      ) : (
      <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Zoho Books Integration</h1>
            <p className="text-wt-text-muted mt-1">Sync accounting data with Zoho Books</p>
          </div>
        </div>
        <Badge 
          className={isConnected 
            ? 'bg-green-500/20 text-green-400 border-green-500/30 text-sm px-3 py-1' 
            : 'bg-red-500/20 text-red-400 border-red-500/30 text-sm px-3 py-1'
          }
        >
          {isConnected ? (
            <><CheckCircle2 className="h-4 w-4 mr-1" /> Connected</>
          ) : (
            <><XCircle className="h-4 w-4 mr-1" /> Disconnected</>
          )}
        </Badge>
      </div>
      
      {/* Connection Status Alert */}
      {!isConnected && status?.error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium">Connection Error</p>
            <p className="text-sm text-wt-text-muted mt-1">{status.error}</p>
          </div>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileText className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.purchaseOrders}</p>
              <p className="text-sm text-wt-text-muted">Purchase Orders</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Building2 className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.vendors}</p>
              <p className="text-sm text-wt-text-muted">Vendors</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <BookOpen className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.bills}</p>
              <p className="text-sm text-wt-text-muted">Bills Created</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Connection Details</h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-wt-border/50">
              <span className="text-wt-text-muted">Organization ID</span>
              <span className="text-wt-text-primary font-mono text-sm">{status?.organization_id || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-wt-border/50">
              <span className="text-wt-text-muted">Data Center</span>
              <span className="text-wt-text-primary">{status?.data_center || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-wt-border/50">
              <span className="text-wt-text-muted">Token Status</span>
              <Badge className={isConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                {isConnected ? 'Valid' : 'Invalid/Expired'}
              </Badge>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-wt-text-muted">Last Sync</span>
              <span className="text-wt-text-primary text-sm">{status?.last_sync || 'Never'}</span>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleRefreshToken}
                disabled={syncing}
                variant="outline"
                className="flex-1 border-wt-border text-wt-text-primary"
              >
                {syncing ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Refreshing...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Refresh Token</>
                )}
              </Button>
              <Button
                onClick={checkStatus}
                variant="outline"
                className="border-wt-border text-wt-text-primary"
              >
                <TestTube className="h-4 w-4 mr-2" /> Test
              </Button>
            </div>
            
            <div className="pt-4 border-t border-wt-border mt-4">
              <Button
                onClick={() => {
                  const next = !showCredentials;
                  setShowCredentials(next);
                  if (next) loadTaxes();
                }}
                variant="ghost"
                className="w-full justify-start text-wt-text-muted hover:text-wt-text-primary"
              >
                <Key className="h-4 w-4 mr-2" />
                {showCredentials ? 'Hide Credentials' : 'Change/Update Credentials'}
              </Button>
            </div>
          </div>
        </motion.div>
        
        {/* Credentials Form (shown when showCredentials is true) */}
        {showCredentials && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-surface border border-wt-border rounded-xl p-6 lg:col-span-2"
          >
            <h2 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Zoho API Credentials
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Client ID *</Label>
                <Input
                  value={credentials.client_id}
                  onChange={(e) => setCredentials({...credentials, client_id: e.target.value})}
                  placeholder="1000.XXXXXXXXXXXXXXXX"
                  className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Client Secret</Label>
                <div className="relative mt-1">
                  <Input
                    type={showClientSecret ? 'text' : 'password'}
                    value={credentials.client_secret}
                    onChange={(e) => setCredentials({...credentials, client_secret: e.target.value})}
                    placeholder="Leave empty to keep existing"
                    className="bg-wt-paper border-wt-border text-wt-text-primary pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowClientSecret(!showClientSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-text-muted"
                  >
                    {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Refresh Token</Label>
                <Input
                  type="password"
                  value={credentials.refresh_token}
                  onChange={(e) => setCredentials({...credentials, refresh_token: e.target.value})}
                  placeholder="Leave empty to keep existing"
                  className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                  data-testid="zoho-refresh-token-input"
                />
                {/* Self-service: paste the 10-min grant code from
                    api-console.zoho.com → Generate Code, and the app
                    exchanges it for a permanent refresh_token. Saves the
                    operator from running curl. */}
                <div className="mt-3 p-3 rounded-lg border border-dashed border-wt-border bg-wt-paper/40">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-wt-text-primary text-xs uppercase tracking-wide">
                      Or auto-generate from grant code
                    </Label>
                    <a
                      href="https://api-console.zoho.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-wt-gold hover:underline inline-flex items-center gap-1"
                      data-testid="zoho-api-console-link"
                    >
                      Open Zoho API Console <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <p className="text-[11px] text-wt-text-muted mb-2 leading-relaxed">
                    In Zoho API Console → your Self Client → <b>Generate Code</b> tab. Scope:
                    <code className="mx-1 px-1 bg-wt-paper/70 rounded">ZohoBooks.fullaccess.all</code>,
                    Duration: 10 minutes. Paste the code below — we'll exchange it for a permanent refresh token.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={grantCode}
                      onChange={(e) => setGrantCode(e.target.value)}
                      placeholder="1000.xxxxxxxxxxxxxxxx (10-min grant code)"
                      className="bg-wt-paper border-wt-border text-wt-text-primary flex-1"
                      data-testid="zoho-grant-code-input"
                    />
                    <Button
                      type="button"
                      onClick={handleExchangeGrantCode}
                      disabled={exchangingCode || !grantCode.trim()}
                      className="bg-wt-gold text-wt-paper hover:bg-wt-gold/90"
                      data-testid="zoho-exchange-grant-code-btn"
                    >
                      {exchangingCode ? (
                        <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Exchanging…</>
                      ) : (
                        <><Key className="h-4 w-4 mr-1" /> Exchange &amp; Save</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Organization ID *</Label>
                <Input
                  value={credentials.organization_id}
                  onChange={(e) => setCredentials({...credentials, organization_id: e.target.value})}
                  placeholder="807870858"
                  className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Data Center</Label>
                <select
                  value={credentials.data_center}
                  onChange={(e) => setCredentials({...credentials, data_center: e.target.value})}
                  className="w-full bg-wt-paper border border-wt-border text-wt-text-primary rounded-md px-3 py-2 mt-1"
                >
                  <option value="com">.com (US)</option>
                  <option value="in">.in (India)</option>
                  <option value="eu">.eu (Europe)</option>
                  <option value="com.au">.com.au (Australia)</option>
                </select>
              </div>
            </div>

            {/* Default purchase tax — applied to every PO line item
                and stamped onto newly-created purchasable items so
                the resulting bill is NOT tax-exempt. Optional but
                strongly recommended. Auto-populated from Zoho when
                credentials already exist; manual entry remains
                available for the first-time setup case. */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs uppercase tracking-wide text-wt-text-muted">
                  Default Purchase Tax
                  <span className="ml-1 text-wt-text-muted/70 normal-case">(optional, recommended)</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setTaxManualEntry(m => !m);
                    if (taxManualEntry) loadTaxes();
                  }}
                  className="text-[11px] text-wt-gold hover:underline"
                  data-testid="zoho-tax-toggle-manual"
                >
                  {taxManualEntry ? 'Pick from list' : 'Enter ID manually'}
                </button>
              </div>
              {!taxManualEntry ? (
                <>
                  <div className="flex items-center gap-2">
                    <select
                      value={credentials.default_purchase_tax_id}
                      onChange={(e) =>
                        setCredentials({ ...credentials, default_purchase_tax_id: e.target.value })
                      }
                      disabled={taxesLoading || taxes.length === 0}
                      className="flex-1 px-3 py-2 rounded-lg bg-wt-paper border border-wt-border text-wt-text-primary focus:outline-none focus:border-wt-gold text-sm disabled:opacity-60"
                      data-testid="zoho-default-purchase-tax-select"
                    >
                      <option value="">— No default tax —</option>
                      {taxes.map(t => (
                        <option key={t.tax_id} value={t.tax_id}>
                          {t.name}
                          {typeof t.percentage === 'number' ? ` (${t.percentage}%)` : ''}
                          {t.type ? ` · ${t.type}` : ''}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={loadTaxes}
                      disabled={taxesLoading}
                      className="border-wt-border text-wt-text-muted"
                      data-testid="zoho-taxes-refresh-btn"
                      title="Refresh tax list from Zoho"
                    >
                      {taxesLoading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <RefreshCw className="h-4 w-4" />}
                    </Button>
                  </div>
                  {taxesLoading && (
                    <p className="mt-1 text-[11px] text-wt-text-muted">Loading taxes from Zoho…</p>
                  )}
                  {!taxesLoading && taxesError && (
                    <p className="mt-1 text-[11px] text-amber-700">
                      {taxesError}. Save the rest of your credentials first, then refresh — or use “Enter ID manually”.
                    </p>
                  )}
                  {!taxesLoading && !taxesError && taxes.length === 0 && (
                    <p className="mt-1 text-[11px] text-wt-text-muted">
                      No taxes returned yet. Save your credentials, then click refresh.
                    </p>
                  )}
                  {!taxesLoading && taxes.length > 0 && (
                    <p className="mt-1 text-[11px] text-wt-text-muted">
                      Pulled <b>{taxes.length}</b> taxes from Zoho Books.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={credentials.default_purchase_tax_id}
                    onChange={(e) =>
                      setCredentials({ ...credentials, default_purchase_tax_id: e.target.value.trim() })
                    }
                    placeholder="e.g. 4815000000123456"
                    className="w-full px-3 py-2 rounded-lg bg-wt-paper border border-wt-border text-wt-text-primary focus:outline-none focus:border-wt-gold font-mono text-sm"
                    data-testid="zoho-default-purchase-tax-id"
                  />
                  <p className="mt-1 text-[11px] text-wt-text-muted">
                    Find this under <b>Zoho Books → Settings → Taxes</b>. Click your VAT/GST tax — the URL contains <code>tax_id=…</code>. Leave blank to keep current behaviour.
                  </p>
                </>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-wt-border">
              <Button
                variant="outline"
                onClick={() => setShowCredentials(false)}
                className="border-wt-border text-wt-text-muted"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCredentials}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Credentials</>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-wt-text-muted mt-4">
              Get your API credentials from the <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer" className="text-wt-gold hover:underline">Zoho API Console</a>. 
              You need a Self Client with scope: ZohoBooks.fullaccess.all
            </p>
          </motion.div>
        )}

        {/* Standalone Default Purchase Tax card — lives outside the
            credentials editor so admins can refresh and pick a default
            tax without unlocking edit mode. The actual PUT is partial:
            only `default_purchase_tax_id` is sent, every other Zoho
            credential field is preserved by the backend. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
          data-testid="zoho-default-tax-card"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-wt-text-primary">Default Purchase Tax</h2>
              <p className="text-xs text-wt-text-muted mt-0.5">
                Stamped onto every PO line item and newly-created purchasable item so the resulting Zoho bill is NOT tax-exempt.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setDefaultTaxManualEntry((m) => !m);
                if (defaultTaxManualEntry) loadTaxes();
              }}
              className="text-[11px] text-wt-gold hover:underline shrink-0"
              data-testid="zoho-default-tax-toggle-manual"
            >
              {defaultTaxManualEntry ? 'Pick from list' : 'Enter ID manually'}
            </button>
          </div>

          {!defaultTaxManualEntry ? (
            <div className="flex items-center gap-2">
              <select
                value={defaultTaxId}
                onChange={(e) => setDefaultTaxId(e.target.value)}
                disabled={taxesLoading || taxes.length === 0}
                className="flex-1 px-3 py-2 rounded-lg bg-wt-paper border border-wt-border text-wt-text-primary focus:outline-none focus:border-wt-gold text-sm disabled:opacity-60"
                data-testid="zoho-default-tax-select-standalone"
              >
                <option value="">— No default tax —</option>
                {taxes.map((t) => (
                  <option key={t.tax_id} value={t.tax_id}>
                    {t.name}
                    {typeof t.percentage === 'number' ? ` (${t.percentage}%)` : ''}
                    {t.type ? ` · ${t.type}` : ''}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadTaxes}
                disabled={taxesLoading}
                className="border-wt-border text-wt-text-muted"
                data-testid="zoho-default-tax-refresh-standalone"
                title="Refresh tax list from Zoho"
              >
                {taxesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                onClick={handleSaveDefaultTax}
                disabled={savingDefaultTax || defaultTaxId === (credentials.default_purchase_tax_id || '')}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                data-testid="zoho-default-tax-save-standalone"
              >
                {savingDefaultTax ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="h-4 w-4 mr-1" /> Save</>
                )}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={defaultTaxId}
                onChange={(e) => setDefaultTaxId(e.target.value.trim())}
                placeholder="e.g. 4815000000123456"
                className="flex-1 px-3 py-2 rounded-lg bg-wt-paper border border-wt-border text-wt-text-primary focus:outline-none focus:border-wt-gold font-mono text-sm"
                data-testid="zoho-default-tax-id-standalone"
              />
              <Button
                type="button"
                onClick={handleSaveDefaultTax}
                disabled={savingDefaultTax || defaultTaxId === (credentials.default_purchase_tax_id || '')}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                data-testid="zoho-default-tax-save-manual-standalone"
              >
                {savingDefaultTax ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="h-4 w-4 mr-1" /> Save</>
                )}
              </Button>
            </div>
          )}

          {!defaultTaxManualEntry && (
            <>
              {taxesLoading && (
                <p className="mt-1 text-[11px] text-wt-text-muted">Loading taxes from Zoho…</p>
              )}
              {!taxesLoading && taxesError && (
                <p className="mt-1 text-[11px] text-amber-700">
                  {taxesError}. Check credentials, then click refresh — or use "Enter ID manually".
                </p>
              )}
              {!taxesLoading && !taxesError && taxes.length === 0 && (
                <p className="mt-1 text-[11px] text-wt-text-muted">
                  No taxes returned yet. Click refresh.
                </p>
              )}
              {!taxesLoading && taxes.length > 0 && (
                <p className="mt-1 text-[11px] text-wt-text-muted">
                  Pulled <b>{taxes.length}</b> taxes from Zoho Books.
                </p>
              )}
            </>
          )}
          {defaultTaxManualEntry && (
            <p className="mt-1 text-[11px] text-wt-text-muted">
              Find this under <b>Zoho Books → Settings → Taxes</b>. Click your VAT/GST tax — the URL contains <code>tax_id=…</code>.
            </p>
          )}
        </motion.div>
        
        {/* Sync Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Sync Actions</h2>
          
          <div className="space-y-3">
            <Button
              onClick={handleSyncVendors}
              disabled={syncing || !isConnected}
              className="w-full bg-wt-surface border border-wt-border text-wt-text-primary hover:bg-wt-paper justify-start"
            >
              <Building2 className="h-4 w-4 mr-2 text-green-400" />
              Sync Vendors from Zoho
            </Button>
            
            <Button
              disabled={!isConnected}
              className="w-full bg-wt-surface border border-wt-border text-wt-text-primary hover:bg-wt-paper justify-start"
            >
              <FileText className="h-4 w-4 mr-2 text-blue-400" />
              Sync Chart of Accounts
            </Button>
            
            <div className="mt-6 pt-4 border-t border-wt-border">
              <h3 className="text-sm font-medium text-wt-text-primary mb-3">Supported Operations</h3>
              <ul className="space-y-2 text-sm text-wt-text-muted">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  Create Purchase Orders in Zoho
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  Sync Vendors bidirectionally
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  Create Bills from GRNs
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  Map Items to Zoho Items
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Documentation Link */}
      <div className="flex justify-center">
        <Button
          variant="link"
          onClick={() => window.open('https://www.zoho.com/books/api/v3/', '_blank')}
          className="text-wt-text-muted hover:text-wt-gold"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View Zoho Books API Documentation
        </Button>
      </div>
      </>
      )}
    </div>
  );
};

export default ZohoIntegrationPage;
